import { useEffect, useRef, useState } from 'react';
import { StationeryDraftFormat } from '@graphql/generated-base';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { withWindow } from '@shared/utils/withWindow';
import { addError } from '@shared/utils/logger';
import { useUpdateStationeryDraftProductMutation } from '@apps/card/Card.graphql.generated';
import { useUpdateStationeryDraftMutation } from '../../CardCustomizer.graphql.generated';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useEditingMode } from '../../useEditingMode';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useCustomizationContext } from '../../useCardCustomization';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useDraftSaverCancel } from '../../useDraftSaver';

type UseProductChoiceGroupArgs = {
  draftId: string;
};

export const useProductChoiceGroup = ({ draftId }: UseProductChoiceGroupArgs) => {
  const [themeId] = useCustomizationContext(d => d.template.themeId);
  const [draftConfig] = useCustomizationContext(d => d.config);
  const { products, activeProduct } = useStationeryProductPricingContext();
  const { mode } = useEditingMode();
  const { customizerProductChoiceGroupClicked } = useCardTelemetry();
  const { goToCustomizeDraft, goToDigitalCustomizeDraft } = useCardsRouterContext();
  const { eventInfo } = useEventInfo();
  const cancelPendingSave = useDraftSaverCancel();

  const [updateDraftProduct] = useUpdateStationeryDraftProductMutation();
  const [saveDraft] = useUpdateStationeryDraftMutation();

  const [switching, setSwitching] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Product types a draft can switch between via updateStationeryDraftProduct
  // (card_service's resolveTargetProductConfig allowlist). Magnet is
  // print-format like the postcard product on the same templates — any two of
  // these on one template make the toggle meaningful (a postcard + magnet
  // template has no digital sibling, e.g.).
  const switchableProducts = products.filter(p => p.productType === 'stationeryCard' || p.productType === 'digitalCard' || p.productType === 'magnet');

  // The free Basic Digital V2 product is a client-side synthetic with no price config, so
  // card_service cannot switch a draft onto it -- `resolveTargetProductConfig` rejects it,
  // and it is not among the template's real products either. Rendering it as a choice
  // would offer a row that always errors, and switching *away* from free is the upsell
  // flow rather than a plain format swap.
  const isFreeProduct = activeProduct.productType === 'basicDigitalV2';

  const canSelectProduct = mode === 'draft' && switchableProducts.length >= 2 && !isFreeProduct;

  const selectProduct = useEventCallback(async (targetProductId: string) => {
    if (!canSelectProduct || switching || targetProductId === activeProduct.id) return;
    if (!eventInfo?.eventId) return;

    const targetProduct = products.find(p => p.id === targetProductId);
    if (!targetProduct) return;

    const toTelemetryLabel = (productType: string): 'paper' | 'premiumDigital' | 'magnet' => {
      if (productType === 'magnet') return 'magnet';
      return productType === 'stationeryCard' ? 'paper' : 'premiumDigital';
    };
    const from = toTelemetryLabel(activeProduct.productType);
    const to = toTelemetryLabel(targetProduct.productType);

    setSwitching(true);
    // Held true through a same-format reload: releasing the switching lock in
    // `finally` would let a second concurrent switch fire during the reload
    // delay and corrupt the draft mid-unload.
    let keepLockForReload = false;
    try {
      // Cancel any pending throttled save FIRST: awaiting the explicit save
      // yields the event loop, and a still-scheduled throttled save could fire
      // mid-switch and overwrite the converted cardJSON with the old payload.
      cancelPendingSave();

      // Persist the latest local edits before switching formats so the BE converts
      // the most recent cardJSON, not whatever was last throttle-saved.
      await saveDraft({
        variables: { payload: { id: draftId, eventId: eventInfo.eventId, cardJSON: draftConfig } },
      });

      const result = await updateDraftProduct({
        variables: { data: { id: draftId, productId: targetProductId } },
      });

      // Record the switch only once the mutation succeeded — firing earlier
      // counts failed attempts as successful switches.
      customizerProductChoiceGroupClicked({ from, to, themeId, draftId });

      const newFormat = result.data?.updateStationeryDraftProduct.format;
      const isPrintType = (productType: string) => productType === 'stationeryCard' || productType === 'magnet';
      const sameFormatSwitch = isPrintType(activeProduct.productType) === isPrintType(targetProduct.productType);
      if (sameFormatSwitch) {
        // Same-format switch (postcard ↔ magnet, both print): navigation lands
        // on the already-mounted customizer, and its draft query is fetchPolicy
        // 'no-cache' — the mutation result cannot update it, so the stale
        // product would keep driving steps, previews, and checkout. Hard-reload;
        // the switch is fully persisted server-side. The short delay lets the
        // telemetry request above flush — an immediate reload aborts it.
        keepLockForReload = true;
        withWindow(global => {
          global.setTimeout(() => global.location.reload(), 300);
        });
        return;
      }
      if (newFormat === StationeryDraftFormat.print) {
        goToCustomizeDraft(draftId);
      } else if (newFormat === StationeryDraftFormat.digital) {
        goToDigitalCustomizeDraft(draftId);
      }
    } catch (error) {
      addError(error, { context: 'useProductChoiceGroup', draftId, targetProductId });
    } finally {
      if (isMountedRef.current && !keepLockForReload) {
        setSwitching(false);
      }
    }
  });

  return { selectProduct, isSwitching: switching, canSelectProduct };
};
